import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const QuickLocationModal = ({ pet, onClose, onSuccess }) => {
  const [locationData, setLocationData] = useState({
    location_area: pet.location_area,
    location_detail: pet.location_detail || '',
    bypass_keepsake_check: false
  });
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [showKeepsakeConfirmation, setShowKeepsakeConfirmation] = useState(false);
  const [keepsakeData, setKeepsakeData] = useState(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await apiClient.get('/config/locations');
      setLocations(response.data.locations);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('🔄 Workflow update started');
    console.log('📝 Workflow data:', locationData);

    try {
      await apiClient.put(`/pets/${pet.id}/location`, locationData);
      onSuccess();
    } catch (error) {
      console.error('Failed to update location:', error);
      
      // Handle keepsake confirmation requirement (409 status)
      if (error.response?.status === 409 && error.response?.data?.detail?.type === 'keepsake_confirmation_required') {
        setKeepsakeData(error.response.data.detail);
        setShowKeepsakeConfirmation(true);
        setLoading(false);
        return;
      }
      
      // Handle other specific business rule errors
      if (error.response?.status === 400 && error.response?.data?.detail) {
        const errorMessage = error.response.data.detail;
        if (errorMessage.toLowerCase().includes('keepsakes')) {
          alert(`❌ Location Update Failed\n\n${errorMessage}\n\nPlease ensure all keepsakes are collected before moving to cremation.`);
        } else {
          alert(`Failed to update location: ${errorMessage}`);
        }
      } else {
        alert('Failed to update location. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleKeepsakeConfirmed = async () => {
    setShowKeepsakeConfirmation(false);
    setLoading(true);
    
    try {
      // Retry with bypass flag
      await apiClient.put(`/pets/${pet.id}/location`, {
        ...locationData,
        bypass_keepsake_check: true
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to update location after confirmation:', error);
      alert('Failed to update location. Please try again.');
      setLoading(false);
    }
  };

  const handleKeepsakeCancelled = () => {
    setShowKeepsakeConfirmation(false);
    setKeepsakeData(null);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Update Location</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <div className="flex items-center mb-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 flex items-center justify-center mr-3">
              <span className="text-white font-medium text-sm">
                {pet.pet_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="font-medium text-gray-900">{pet.pet_name}</div>
              <div className="text-sm text-gray-500">{pet.client_name}</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Area *</label>
            <select
              required
              value={locationData.location_area}
              onChange={(e) => setLocationData({ ...locationData, location_area: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            >
              {locations.map((location) => (
                <option key={location} value={location}>{location}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location Detail</label>
            <input
              type="text"
              placeholder="e.g., Shelf B2, Unit 400"
              value={locationData.location_detail}
              onChange={(e) => setLocationData({ ...locationData, location_detail: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900"
            />
            <p className="text-xs text-gray-500 mt-1">Optional specific location within the area</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Location'}
            </button>
          </div>
        </form>

        {/* Keepsake Confirmation Dialog - FOR LOCATION TRANSFERS */}
        {showKeepsakeConfirmation && keepsakeData && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60">
            <div className="bg-white rounded-xl max-w-md w-full mx-4 p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
                  <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  ⚠️ Keepsakes Must Be Collected First
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This pet has selected keepsakes that must be collected before moving to cremation.
                </p>
                <div className="bg-yellow-50 p-3 rounded-lg mb-4">
                  <p className="text-xs font-medium text-yellow-800 mb-2">Pet's Selected Keepsakes:</p>
                  <ul className="text-xs text-yellow-700">
                    {(keepsakeData.keepsakes || []).map((keepsake, index) => (
                      <li key={index}>• {keepsake}</li>
                    ))}
                  </ul>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleKeepsakeCancelled}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel Transfer
                  </button>
                  <button
                    onClick={handleKeepsakeConfirmed}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    ✅ Confirm: Keepsakes Collected
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default QuickLocationModal;
